/* =========================================================
 * CEL | Blog search
 *
 * Filters the recipe grid in place as the visitor types. No form, no submit,
 * no navigation: the cards already on the page are shown and hidden, so the
 * result is instant and the scroll position is kept.
 *
 * The grid is paginated server-side, so the cards on screen are only the
 * current page. On the first keystroke the remaining pages are pulled in
 * through the Section Rendering API and their cards are added to an in-memory
 * index, which is what makes the search cover every recipe rather than the
 * handful currently rendered. Nothing is fetched until the visitor actually
 * types, so a visitor who never searches pays nothing for this.
 *
 * All wording and configuration arrives on data attributes from
 * sections/main-blog_cel.liquid. This file is a static asset and cannot read
 * the locale files, so it must not contain visible copy.
 * ========================================================= */
(() => {
  // Long enough to skip the intermediate states of a fast typist, short enough
  // that the list still feels like it is reacting to the keystroke.
  const DEBOUNCE_MS = 150;

  /* Lowercases, flattens accents and collapses whitespace, so "açaí" is found
   * by typing "acai" and a stray double space in a title does not stop a
   * two-word query from matching. */
  const normalise = (value) =>
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  /* The searchable text of one card.
   *
   * Not `textContent`, which concatenates with nothing in between and glues the
   * tag onto the title: the Treats card comes out as "treatsmunchies protein
   * cookie dough", so a visitor typing "treats munchies" finds nothing. Joining
   * on a space at every level puts a boundary between the two.
   *
   * Not `innerText` either, tempting as it is: it triggers layout, and it
   * returns nothing at all for a hidden element — every card pulled in from a
   * later page arrives hidden, so their text would come back empty. */
  const cardText = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    return Array.from(node.childNodes).map(cardText).join(' ');
  };

  const debounce = (fn, wait) => {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  class BlogSearch {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-blog-search-input]');
      this.status = root.querySelector('[data-blog-search-status]');

      /* The results wrapper is a sibling rather than a descendant: the search
       * field sits above the grid, and the grid lives inside the paginate tag.
       * Scoped to the same section so a second blog section on the page could
       * not be filtered by this one's field. */
      const section = root.closest('.shopify-section') || document;
      this.results = section.querySelector('[data-blog-search-results]');
      this.list = this.results && this.results.querySelector('blog-post-list');
      this.pagination =
        this.results && this.results.querySelector('.pagination_cel');

      if (!this.input || !this.list) return;

      /* Marks the field as wired up, and refuses to wire it up twice.
       *
       * A second instance on the same field would bind its own input listener,
       * fetch the remaining pages again and append a second copy of every
       * off-page recipe, so the grid would show duplicates. Worth guarding
       * rather than assuming: init runs both on DOM ready and on the theme
       * editor's section:load, and it costs one attribute to make the pairing
       * safe. */
      if (root.dataset.blogSearchReady === 'true') return;
      root.dataset.blogSearchReady = 'true';

      this.sectionId = root.dataset.blogSearchSection || '';
      this.totalPages = parseInt(this.results.dataset.blogSearchPages, 10) || 1;

      this.emptyText = root.dataset.blogSearchEmpty || '';
      this.countOne = root.dataset.blogSearchCountOne || '';
      this.countOther = root.dataset.blogSearchCountOther || '';

      /* Direct-child anchors only, not every child. 'blog-post-card_cel' emits
       * a <style> block alongside each card, so the grid's children alternate
       * between a style element and a card. Indexing children blindly would put
       * CSS text in the haystack — typing "margin" would match — and hiding a
       * style element would take one card's rules with it. */
      this.index = Array.from(
        this.list.querySelectorAll(':scope > a.blog-post-card_cel')
      ).map((card) => ({
        card,
        haystack: normalise(cardText(card)),

        /* Cards rendered by the server belong to the current page and are
         * visible when no query is active. Cards pulled in from later pages are
         * appended hidden and only ever surface as a search result, so clearing
         * the field must not reveal them. */
        onPage: true,
      }));

      // Set once the other pages have been merged in; until then the index only
      // covers the current page and is re-filtered when the rest arrives.
      this.remainingPagesRequest = null;

      this.input.addEventListener(
        'input',
        debounce(() => this.handleInput(), DEBOUNCE_MS)
      );

      /* Escape clears the field. Handled here rather than left to the browser
       * because the native clear only exists on some platforms, and clearing
       * has to re-run the filter either way. */
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.input.value !== '') {
          event.preventDefault();
          this.input.value = '';
          this.handleInput();
        }
      });

      /* The field keeps its value across a back/forward navigation and a
       * reload, so filter once on load to match what is in it. */
      if (this.input.value.trim() !== '') this.handleInput();
    }

    handleInput() {
      const query = normalise(this.input.value);

      if (query === '') {
        this.restore();
        return;
      }

      this.apply(query);

      // Kicked off on the first real keystroke, then awaited once. When it
      // resolves the filter is re-run so the newly indexed pages are included.
      if (!this.remainingPagesRequest) {
        this.remainingPagesRequest = this.loadRemainingPages();
        this.remainingPagesRequest.then(() => {
          const current = normalise(this.input.value);
          if (current !== '') this.apply(current);
        });
      }
    }

    /* Every whitespace-separated term has to appear somewhere in the card, so
     * "berry smoothie" narrows rather than widens the result. Substring rather
     * than whole-word matching, so a partly typed word still matches. */
    matches(haystack, terms) {
      return terms.every((term) => haystack.includes(term));
    }

    apply(query) {
      const terms = query.split(' ').filter(Boolean);
      let count = 0;

      this.index.forEach((entry) => {
        const hit = this.matches(entry.haystack, terms);
        entry.card.hidden = !hit;
        if (hit) count += 1;
      });

      /* Pagination belongs to the unfiltered list. Its links would page through
       * the blog and throw the search away, and its page numbers would be wrong
       * for the filtered set, so it is hidden while a query is active. */
      if (this.pagination) this.pagination.hidden = true;

      this.setStatus(count === 0 ? this.emptyText : this.formatCount(count));
    }

    restore() {
      this.index.forEach((entry) => {
        entry.card.hidden = !entry.onPage;
      });

      if (this.pagination) this.pagination.hidden = false;
      this.setStatus('');
    }

    /* The section hands over the whole sentence with __count__ standing in for
     * the number, so word order and pluralisation stay in the locale file.
     * `{{ count }}` is accepted as well: that is the form the locale file
     * actually contains, and it reaches here intact if the sentence is ever
     * passed through somewhere Liquid can render it. */
    formatCount(count) {
      const template = count === 1 ? this.countOne : this.countOther;
      if (!template) return '';
      return template
        .replace(/__count__/g, String(count))
        .replace(/\{\{\s*count\s*\}\}/g, String(count));
    }

    setStatus(text) {
      if (!this.status) return;
      this.status.textContent = text;
    }

    /* Builds the URL for one page of the list as it is currently filtered.
     *
     * Derived from the address bar rather than assembled from the blog handle,
     * because that already carries whichever tag is being viewed — the tag is
     * part of the path (/blogs/recipes/tagged/breakfast) — so the fetched pages
     * match the visible list instead of covering the whole blog.
     *
     * section_id asks Shopify for this section on its own. Without it each page
     * would come back as a whole document, header and footer included, which is
     * several times the payload for the same handful of cards. */
    pageUrl(page) {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('page', String(page));
      if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    async loadRemainingPages() {
      if (this.totalPages <= 1) return;

      const pages = [];
      for (let page = 2; page <= this.totalPages; page += 1) pages.push(page);

      /* allSettled, not all: one failed page should cost that page's recipes,
       * not the whole search. Fetched in parallel because they are independent
       * and the visitor is waiting. */
      const responses = await Promise.allSettled(
        pages.map((page) =>
          fetch(this.pageUrl(page), {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          }).then((response) => {
            if (!response.ok) throw new Error(String(response.status));
            return response.text();
          })
        )
      );

      let anyFailed = false;

      // Every recipe already indexed, so the same one is not added twice.
      const seen = new Set(
        this.index.map((entry) => entry.card.getAttribute('href'))
      );

      /* allSettled preserves the order the requests were made in, not the order
       * they came back, so walking it appends the pages in the blog's own order
       * without any sorting. */
      responses.forEach((result) => {
        if (result.status !== 'fulfilled') {
          anyFailed = true;
          return;
        }

        const doc = new DOMParser().parseFromString(result.value, 'text/html');

        /* Scoped to the grid rather than the whole response. The same card
         * snippet also renders the featured recipe in the hero, which belongs to
         * that hero and not to this list — without the scope it would be indexed
         * and then appear a second time inside the grid as a search result. */
        const found = doc.querySelectorAll(
          'blog-post-list > a.blog-post-card_cel'
        );

        found.forEach((card) => {
          const href = card.getAttribute('href');

          /* Guards against the same recipe being indexed twice, which would
           * show it twice in the filtered grid. Possible if the article count
           * changes between the page render and these fetches, shifting an
           * article across the page boundary. */
          if (!href || seen.has(href)) return;
          seen.add(href);

          const imported = document.importNode(card, true);

          /* Appended to the real grid, not held aside, so a match only has to be
           * unhidden and lands in the layout the rest of the cards already use.
           * Hidden on arrival: it is not part of this page of the blog and must
           * stay out of sight until it matches something.
           *
           * The <style> sibling the snippet emits is deliberately not imported.
           * It is the same rules for every card and the copies already on the
           * page cover these too. */
          imported.hidden = true;
          this.list.appendChild(imported);

          this.index.push({
            card: imported,
            haystack: normalise(cardText(imported)),
            onPage: false,
          });
        });
      });

      /* Left unset so the next keystroke tries again. Only matters when a fetch
       * actually failed; a complete load is never repeated. */
      if (anyFailed) this.remainingPagesRequest = null;
    }
  }

  const init = (scope = document) => {
    scope
      .querySelectorAll('[data-blog-search]')
      .forEach((root) => new BlogSearch(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* The theme editor replaces a section's markup outright when a setting
   * changes, which takes the wired-up field with it. Re-initialising the
   * replaced section keeps the search working while an editor is adjusting the
   * grid, instead of appearing broken until they reload. */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) init(event.target);
  });
})();
